import React from 'react'
import { FieldValues, UseFormReturn } from 'react-hook-form'
import CheckboxSingle from '../components/CheckboxSingle'
import InformationItems from '../components/InformationItems'
import { addUserEducation } from '../api/addUserEducation'
import { deleteUserEducation } from '../api/deleteUserEducation'
import { updateUserEducation } from '../api/updateUserEducation'
import EducationInfoItem from '../components/EducationInfoItem'
import { getUserEducationList } from '../api/getUserEducationList'
import SelectForForm from '../components/SelectForForm'
import { ERROR_DICT, errorDict } from '../utils/errorDict'
import { axiosInstance, useAxios } from '../hooks/useAxios'
import { getReference } from '../api/getReference'
import TextField from '../components/TextField'

function ProfileEditEducationPage(): React.ReactElement {
  const { response: schoolTypes, fire: schoolTypesFire } = useAxios()
  const { response: eduForms, fire: eduFormsFire } = useAxios()
  const { response: eduBasements, fire: eduBasementsFire } = useAxios()
  const { response: eduLevels, fire: eduLevelsFire } = useAxios()

  const preload = async () => {
    await Promise.all([
      schoolTypesFire(getReference('school-types', { all: true })),
      eduFormsFire(getReference('education-forms', { all: true })),
      eduBasementsFire(getReference('education-basements', { all: true })),
      eduLevelsFire(getReference('education-levels', { all: true }))
    ])
  }

  const fields = (
    {
      control,
      formState: { errors },
      watch,
      resetField,
      setValue
    }: UseFormReturn<FieldValues, any>,
    edit = false
  ) => {
    const watchType = watch('type')
    const watchForeign = watch('foreign')
    const watchPresent = watch('present')

    watch((data, { name, type }) => {
      if (name === 'type' && type === 'change') {
        Object.keys(data).forEach((key) => {
          if (!['foreign', 'type'].includes(key)) {
            resetField(key)
          }
        })
      } else if (name === 'foreign' && type === 'change') {
        setValue('university', '')
        setValue('speciality', '')
        setValue('foreignUniversity', '')
        setValue('foreignSpeciality', '')
      }
    })

    const getUniversities = async (query: string) => {
      try {
        const { data } = await axiosInstance(
          getReference('universities', { query })
        )

        return data?.data ?? []
      } catch (e) {
        return []
      }
    }

    const getSpecialities = async (query: string) => {
      try {
        const { data } = await axiosInstance(
          getReference('specialities', { query })
        )

        return data?.data ?? []
      } catch (e) {
        return []
      }
    }

    return (
      <>
        <CheckboxSingle
          className={'text-body-bold'}
          name={'foreign'}
          control={control}
        >
          Обучение за рубежом
        </CheckboxSingle>
        <SelectForForm
          control={control}
          name={'type'}
          label={'Школа/университет'}
          disabled={edit}
          required
          rules={{ required: true }}
          hasError={!!errors.type}
          errorText={errorDict(errors.type)}
          options={[
            { value: 'university', label: 'Университет' },
            { value: 'school', label: 'Школа' }
          ]}
        ></SelectForForm>
        {watchType === 'school' ? (
          <>
            <div className={'row row-gap-20 row-mobile-none'}>
              <TextField
                className={'u-flexGrow1'}
                name={'region'}
                control={control}
                required
                label={'Регион'}
                rules={{
                  validate: (value) => {
                    return !!value || watchType === 'university'
                  }
                }}
                hasError={!!errors.region}
                errorText={ERROR_DICT.required}
              ></TextField>
              <SelectForForm
                fullValue
                control={control}
                name={'schoolType'}
                rules={{
                  validate: (value) => {
                    return !!value || watchType === 'university'
                  }
                }}
                className={'u-flexGrow1'}
                required
                options={schoolTypes?.data ?? []}
                label={'Тип учебного заведения'}
                hasError={!!errors.schoolType}
                errorText={ERROR_DICT.required}
              ></SelectForForm>
            </div>
            <TextField
              className={'u-flexGrow1'}
              name={'school'}
              control={control}
              required
              label={'Название и №'}
              rules={{
                validate: (value) => {
                  return !!value || watchType === 'university'
                }
              }}
              hasError={!!errors.school}
              errorText={ERROR_DICT.required}
            ></TextField>
            <CheckboxSingle
              name={'main'}
              control={control}
              className={'text-body-bold mt-md-4 mt-sm-3'}
            >
              Данные для профиля
            </CheckboxSingle>
          </>
        ) : watchType === 'university' ? (
          <>
            {watchForeign ? (
              <TextField
                control={control}
                name={'foreignUniversity'}
                label={'Наименование'}
                required
                rules={{
                  validate: (value) => {
                    return !!value || watchType === 'school'
                  }
                }}
                hasError={!!errors.foreignUniversity}
                errorText={ERROR_DICT.required}
              ></TextField>
            ) : (
              <SelectForForm
                fullValue
                control={control}
                name={'university'}
                asyncOptions={getUniversities}
                label={'Наименование'}
                required
                rules={{
                  validate: (value) => {
                    return !!value || watchType === 'school'
                  }
                }}
                hasError={!!errors.university}
                errorText={ERROR_DICT.required}
                searchable
              ></SelectForForm>
            )}
            {watchForeign ? (
              <TextField
                control={control}
                name={'foreignSpeciality'}
                label={'Специальность'}
                required
                rules={{
                  validate: (value) => {
                    return !!value || watchType === 'school'
                  }
                }}
                hasError={!!errors.foreignSpeciality}
                errorText={ERROR_DICT.required}
              ></TextField>
            ) : (
              <SelectForForm
                fullValue
                control={control}
                name={'speciality'}
                label={'Специальность'}
                asyncOptions={getSpecialities}
                required
                rules={{
                  validate: (value) => {
                    return !!value || watchType === 'school'
                  }
                }}
                hasError={!!errors.speciality}
                errorText={ERROR_DICT.required}
                searchable
              ></SelectForForm>
            )}
            <div className={'row row-gap-20 row-mobile-none'}>
              <TextField
                className={'u-flexGrow1'}
                control={control}
                rules={{
                  validate: (value) => {
                    return !!value || watchType === 'school'
                  }
                }}
                hasError={!!errors.startYear}
                errorText={ERROR_DICT.required}
                name="startYear"
                datepickerOptions={{
                  view: 'years',
                  minView: 'years',
                  dateFormat: 'yyyy'
                }}
                type="date"
                label="Год начала обучения"
                required
              ></TextField>
              <TextField
                className={'u-flexGrow1'}
                control={control}
                rules={{
                  validate: (value, formValues) => {
                    return (
                      !!value || !!formValues.present || watchType === 'school'
                    )
                  }
                }}
                hasError={!!errors.endYear}
                errorText={ERROR_DICT.required}
                name="endYear"
                datepickerOptions={{
                  view: 'years',
                  minView: 'years',
                  dateFormat: 'yyyy'
                }}
                type="date"
                label="Год окончания обучения"
                required={!watchPresent}
              ></TextField>
            </div>
            <CheckboxSingle
              name={'present'}
              control={control}
              className={'text-body-bold'}
            >
              По настоящее время
            </CheckboxSingle>
            <div className={'row row-gap-20 row-mobile-none'}>
              <SelectForForm
                fullValue
                className={'u-flexGrow1'}
                control={control}
                name={'form'}
                label={'Форма обучения'}
                rules={{
                  validate: (value) => {
                    return !!value || watchType === 'school'
                  }
                }}
                hasError={!!errors.form}
                required
                options={eduForms?.data ?? []}
                errorText={ERROR_DICT.required}
              ></SelectForForm>
              <SelectForForm
                fullValue
                className={'u-flexGrow1'}
                control={control}
                name={'basement'}
                label={'Основа обучения'}
                rules={{
                  validate: (value) => {
                    return !!value || watchType === 'school'
                  }
                }}
                hasError={!!errors.basement}
                required
                options={eduBasements?.data ?? []}
                errorText={ERROR_DICT.required}
              ></SelectForForm>
              <SelectForForm
                fullValue
                className={'u-flexGrow1'}
                control={control}
                name={'level'}
                label={'Уровень образования'}
                rules={{
                  validate: (value) => {
                    return !!value || watchType === 'school'
                  }
                }}
                hasError={!!errors.level}
                required
                options={eduLevels?.data ?? []}
                errorText={ERROR_DICT.required}
              ></SelectForForm>
            </div>
            <CheckboxSingle
              name={'main'}
              control={control}
              className={'text-body-bold'}
            >
              Данные для профиля
            </CheckboxSingle>
          </>
        ) : null}
      </>
    )
  }

  return (
    <InformationItems
      preload={preload}
      addMethod={addUserEducation}
      deleteMethod={deleteUserEducation}
      updateMethod={updateUserEducation}
      updateSuccessText={'Информация об образовании успешно изменена'}
      addSuccessText={'Информация об образовании успешно добавлена'}
      deleteSuccessText={'Информация об образовании успешно удалена'}
      emptyText={'Информация об образовании не найдена'}
      addButtonText={'Добавить место учебы'}
      addTitleText={'Новое место учебы'}
      listMethod={getUserEducationList}
      renderView={(item: any) => {
        return <EducationInfoItem item={item}></EducationInfoItem>
      }}
      renderAdd={fields}
      renderUpdate={(form) => fields(form, true)}
    ></InformationItems>
  )
}

export default ProfileEditEducationPage
